import {useAuth} from '../stores/AuthStore'
import {useAuthClients} from '../queries'

/**
 * True only for users who can switch between multiple camps/clients — i.e.
 * CampMinder employees (and other multi-client users) authenticated via the
 * IdP whose auth-client list has more than one entry. This is the single
 * source of truth for the same predicate `PrimaryNavigation` uses to decide
 * whether to show the camp picker.
 *
 * Wraps `useAuthClients` with a `select` that reduces the payload to a count,
 * so this hook never retains the full client list. The query key is shared
 * with any other `useAuthClients` consumer, so this is a cache read, not an
 * extra network request.
 *
 * NOTE: this hook no longer backs the internal-only "employee" affordance
 * gate. That gate now lives in `useIsCampMinderEmployee`, which keys off the
 * @campminder.com login domain — a tighter proxy for "CampMinder employee"
 * than the multi-client heuristic, which also matched non-employee multi-camp
 * admins.
 */
export const useCanSwitchCamps = (): boolean => {
  const {useIdpAuthentication, authenticationStatus} = useAuth()

  const {data: clientCount, isSuccess} = useAuthClients<number>({
    select: (clients) => clients.length,
    useIdpAuthentication,
  })

  return (
    useIdpAuthentication &&
    authenticationStatus !== 'unauthenticated' &&
    isSuccess &&
    (clientCount ?? 0) > 1
  )
}
